export const SHIPMENT_TYPES = {
  DOMESTIC: "Domestic Shipment",
  IMPORT: "Import Shipment",
  EXPORT: "Export Shipment"
};

export const SERVICE_TYPES = {
  LOGISTICS: "logistics_shipment",
  PICKUP_DELIVERY: "pickup_delivery"
};

const DEFAULT_WORKFLOW = {
  showPrimaryForm: true,
  showDomesticForm: false,
  showImportForm: false,
  showExportForm: false,
  showShipmentTypeSelector: false,
  shouldClearContainerShippingInfo: true,
  allowEmptyReturn: false,
  allowEmptyPickup: false,
  allowSlotRules: false,
  slotDirection: "none",
  slotAmount: 0,
  slotDelta: 0,
  emailTriggers: {
    bookingCompletedReceipt: true,
    emptyReturnNotification: true,
    emptyPickupNotification: true
  },
  workflowSource: "fallback",
  workflowConfigId: ""
};

const normalizeText = (value) => (value || "").toString().trim();

export const normalizeShipmentType = (value) => {
  const normalized = normalizeText(value).toLowerCase().replace(/[_-]+/g, " ");
  if (normalized === "domestic" || normalized === "domestic shipment") {
    return SHIPMENT_TYPES.DOMESTIC;
  }
  if (normalized === "import" || normalized === "import shipment") {
    return SHIPMENT_TYPES.IMPORT;
  }
  if (normalized === "export" || normalized === "export shipment") {
    return SHIPMENT_TYPES.EXPORT;
  }
  return "";
};

export const normalizeServiceType = (value) => {
  const normalized = normalizeText(value).toLowerCase().replace(/[^a-z]+/g, "_").replace(/^_+|_+$/g, "");
  if (normalized === SERVICE_TYPES.PICKUP_DELIVERY || normalized === "pickupdelivery") {
    return SERVICE_TYPES.PICKUP_DELIVERY;
  }
  return SERVICE_TYPES.LOGISTICS;
};

export const getVehicleSize = ({ vehicleSize, vehicleTypeName, carType } = {}) => {
  const explicitSize = normalizeText(vehicleSize).toLowerCase().replace(/\s+/g, "");
  if (explicitSize === "10ft") return "10ft";
  if (explicitSize === "20ft") return "20ft";
  if (explicitSize === "40ft") return "40ft";

  const name = normalizeText(vehicleTypeName || carType).toLowerCase();
  if (/(^|[^0-9])10\s*(ft|footer|feet)?([^0-9]|$)/.test(name)) return "10ft";
  if (/(^|[^0-9])20\s*(ft|footer|feet)?([^0-9]|$)/.test(name)) return "20ft";
  if (/(^|[^0-9])40\s*(ft|footer|feet)?([^0-9]|$)/.test(name)) return "40ft";
  return "";
};

export const is20ftOr40ft = (vehicleSize) => {
  const size = getVehicleSize({ vehicleSize });
  return size === "20ft" || size === "40ft";
};

export const isDomesticShipment = (shipmentType) => normalizeShipmentType(shipmentType) === SHIPMENT_TYPES.DOMESTIC;
export const isImportShipment = (shipmentType) => normalizeShipmentType(shipmentType) === SHIPMENT_TYPES.IMPORT;
export const isExportShipment = (shipmentType) => normalizeShipmentType(shipmentType) === SHIPMENT_TYPES.EXPORT;

const isCompletedStatus = (status) => {
  const normalized = normalizeText(status).toUpperCase();
  return normalized === "COMPLETE" || normalized === "COMPLETED";
};

const normalizeMatchText = (value) => normalizeText(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const workflowConfigsToArray = (workflowConfigs) => {
  if (!workflowConfigs) return [];
  if (Array.isArray(workflowConfigs)) return workflowConfigs;
  if (workflowConfigs.configs && Array.isArray(workflowConfigs.configs)) return workflowConfigs.configs;
  return [workflowConfigs];
};

export const findWorkflowConfig = (workflowConfigs, {
  vehicleTypeId = "",
  vehicleTypeName = "",
  carType = "",
  vehicleSize = "",
  shipmentType = ""
} = {}) => {
  const configs = workflowConfigsToArray(workflowConfigs).filter((config) => config && config.active !== false);
  if (!configs.length) return null;

  const resolvedVehicleTypeId = normalizeText(vehicleTypeId);
  const resolvedVehicleTypeName = normalizeText(vehicleTypeName || carType);
  const resolvedVehicleSize = getVehicleSize({ vehicleSize, vehicleTypeName: resolvedVehicleTypeName, carType: resolvedVehicleTypeName });
  const resolvedShipmentType = normalizeShipmentType(shipmentType) || (resolvedVehicleSize === "10ft" ? SHIPMENT_TYPES.DOMESTIC : "");
  const normalizedName = normalizeMatchText(resolvedVehicleTypeName);

  const shipmentMatches = (config) => {
    const configShipment = normalizeShipmentType(config.shipmentType);
    return resolvedShipmentType ? configShipment === resolvedShipmentType : !configShipment;
  };

  return configs.find((config) => resolvedVehicleTypeId && config.vehicleTypeId === resolvedVehicleTypeId && shipmentMatches(config))
    || configs.find((config) => normalizedName && normalizeMatchText(config.vehicleTypeName) === normalizedName && shipmentMatches(config))
    || configs.find((config) => resolvedVehicleSize && config.vehicleSize === resolvedVehicleSize && shipmentMatches(config))
    || null;
};

const hasMeaningfulData = (value) => {
  if (!value || typeof value !== "object") return false;
  return Object.keys(value).some((key) => {
    const item = value[key];
    return item !== null && item !== undefined && item !== "" && !(Array.isArray(item) && item.length === 0);
  });
};

const getBookingRecordShipmentType = (booking = {}) => {
  const explicitShipment = normalizeShipmentType(booking.shipmentType);
  if (explicitShipment) return explicitShipment;
  if (hasMeaningfulData(booking.importBookingInfo)) return SHIPMENT_TYPES.IMPORT;
  if (hasMeaningfulData(booking.exportBookingInfo)) return SHIPMENT_TYPES.EXPORT;
  if (hasMeaningfulData(booking.domesticBookingInfo)) return SHIPMENT_TYPES.DOMESTIC;
  return "";
};

export const getBookingWorkflowForBookingRecord = (booking = {}, workflowConfigs = null) => {
  const vehicleTypeName = typeof booking.carType === "string"
    ? booking.carType
    : booking.carType && (booking.carType.name || booking.carType.carType || booking.carType.vehicleTypeName)
      ? (booking.carType.name || booking.carType.carType || booking.carType.vehicleTypeName)
      : booking.vehicleTypeName || "";
  const vehicleTypeId = booking.carType && typeof booking.carType === "object" && (booking.carType.id || booking.carType.vehicleTypeId || booking.carType.uid)
    ? (booking.carType.id || booking.carType.vehicleTypeId || booking.carType.uid)
    : booking.vehicleTypeId || "";
  const shipmentType = getBookingRecordShipmentType(booking);
  const vehicleSize = getVehicleSize({
    vehicleSize: booking.vehicleSize || booking.containerSize || (booking.importBookingInfo && booking.importBookingInfo.containerSize) || (booking.exportBookingInfo && booking.exportBookingInfo.containerSize),
    vehicleTypeName,
    carType: vehicleTypeName
  });
  const workflowConfig = findWorkflowConfig(workflowConfigs, {
    vehicleTypeId,
    vehicleTypeName,
    carType: vehicleTypeName,
    vehicleSize,
    shipmentType
  });
  return getBookingWorkflow({
    vehicleTypeId,
    vehicleTypeName,
    carType: vehicleTypeName,
    vehicleSize,
    shipmentType: shipmentType || (vehicleSize === "10ft" ? SHIPMENT_TYPES.DOMESTIC : ""),
    serviceType: booking.serviceType,
    status: booking.status,
    workflowConfig
  });
};

const applyWorkflowConfig = (baseWorkflow, workflowConfig, context) => {
  if (!workflowConfig) {
    return {
      ...baseWorkflow,
      slotDelta: baseWorkflow.slotAmount || 0,
      workflowSource: "fallback",
      workflowConfigId: ""
    };
  }

  const forms = workflowConfig.forms || {
    primary: workflowConfig.formsPrimary,
    domestic: workflowConfig.formsDomestic,
    import: workflowConfig.formsImport,
    export: workflowConfig.formsExport
  };
  const workflows = workflowConfig.workflows || {
    emptyReturn: workflowConfig.workflowsEmptyReturn,
    emptyPickup: workflowConfig.workflowsEmptyPickup,
    slotRules: workflowConfig.workflowsSlotRules
  };
  const slotRule = workflowConfig.slotRule || {
    direction: workflowConfig.slotRuleDirection,
    amount: workflowConfig.slotRuleAmount
  };
  const emailTriggers = workflowConfig.emailTriggers || {
    bookingCompletedReceipt: workflowConfig.emailBookingCompletedReceipt,
    emptyReturnNotification: workflowConfig.emailEmptyReturnNotification,
    emptyPickupNotification: workflowConfig.emailEmptyPickupNotification
  };
  const slotAmount = slotRule.amount !== undefined ? Number(slotRule.amount) || 0 : baseWorkflow.slotAmount;
  const mergedEmailTriggers = {
    ...baseWorkflow.emailTriggers,
    bookingCompletedReceipt: emailTriggers.bookingCompletedReceipt !== undefined
      ? !!emailTriggers.bookingCompletedReceipt
      : baseWorkflow.emailTriggers.bookingCompletedReceipt,
    emptyReturnNotification: emailTriggers.emptyReturnNotification !== undefined
      ? !!emailTriggers.emptyReturnNotification
      : baseWorkflow.emailTriggers.emptyReturnNotification,
    emptyPickupNotification: emailTriggers.emptyPickupNotification !== undefined
      ? !!emailTriggers.emptyPickupNotification
      : baseWorkflow.emailTriggers.emptyPickupNotification
  };

  return {
    ...baseWorkflow,
    showPrimaryForm: forms.primary !== undefined ? !!forms.primary : baseWorkflow.showPrimaryForm,
    showDomesticForm: forms.domestic !== undefined ? !!forms.domestic : baseWorkflow.showDomesticForm,
    showImportForm: forms.import !== undefined ? !!forms.import : baseWorkflow.showImportForm,
    showExportForm: forms.export !== undefined ? !!forms.export : baseWorkflow.showExportForm,
    allowEmptyReturn: workflows.emptyReturn !== undefined
      ? !!workflows.emptyReturn && isCompletedStatus(context.status)
      : baseWorkflow.allowEmptyReturn,
    allowEmptyPickup: workflows.emptyPickup !== undefined ? !!workflows.emptyPickup : baseWorkflow.allowEmptyPickup,
    allowSlotRules: workflows.slotRules !== undefined ? !!workflows.slotRules : baseWorkflow.allowSlotRules,
    slotDirection: slotRule.direction || baseWorkflow.slotDirection,
    slotAmount,
    slotDelta: slotAmount,
    emailTriggers: mergedEmailTriggers,
    workflowSource: "configuration",
    workflowConfigId: workflowConfig.id || workflowConfig.key || workflowConfig.uid || ""
  };
};

export const getBookingWorkflow = ({
  vehicleTypeId = "",
  vehicleTypeName = "",
  carType = "",
  vehicleSize = "",
  shipmentType = "",
  serviceType = "",
  status = "",
  workflowConfig = null
} = {}) => {
  const resolvedVehicleSize = getVehicleSize({ vehicleSize, vehicleTypeName, carType });
  const resolvedShipmentType = normalizeShipmentType(shipmentType);
  const resolvedServiceType = normalizeServiceType(serviceType);
  const isContainer20Or40 = is20ftOr40ft(resolvedVehicleSize);
  const workflow = {
    ...DEFAULT_WORKFLOW,
    vehicleTypeId,
    vehicleTypeName: normalizeText(vehicleTypeName || carType),
    vehicleSize: resolvedVehicleSize,
    shipmentType: resolvedShipmentType,
    serviceType: resolvedServiceType,
    isContainerTruck: resolvedVehicleSize === "10ft" || isContainer20Or40,
    isOtherVehicleType: !resolvedVehicleSize
  };
  workflow.showShipmentTypeSelector = resolvedServiceType === SERVICE_TYPES.LOGISTICS && isContainer20Or40;

  if (resolvedServiceType !== SERVICE_TYPES.LOGISTICS || !resolvedVehicleSize) {
    return applyWorkflowConfig(workflow, workflowConfig, { status });
  }

  if (resolvedVehicleSize === "10ft") {
    workflow.shipmentType = SHIPMENT_TYPES.DOMESTIC;
    workflow.showDomesticForm = true;
    return applyWorkflowConfig(workflow, workflowConfig, { status });
  }

  if (isContainer20Or40 && resolvedShipmentType === SHIPMENT_TYPES.DOMESTIC) {
    workflow.showDomesticForm = true;
    return applyWorkflowConfig(workflow, workflowConfig, { status });
  }

  if (isContainer20Or40 && resolvedShipmentType === SHIPMENT_TYPES.IMPORT) {
    workflow.showImportForm = true;
    workflow.allowEmptyReturn = isCompletedStatus(status);
    workflow.allowSlotRules = true;
    workflow.slotDirection = "deduct";
    workflow.slotAmount = 1;
    workflow.slotDelta = 1;
    return applyWorkflowConfig(workflow, workflowConfig, { status });
  }

  if (isContainer20Or40 && resolvedShipmentType === SHIPMENT_TYPES.EXPORT) {
    workflow.showExportForm = true;
    workflow.allowEmptyPickup = true;
    workflow.allowSlotRules = true;
    workflow.slotDirection = "add";
    workflow.slotAmount = 1;
    workflow.slotDelta = 1;
    return applyWorkflowConfig(workflow, workflowConfig, { status });
  }

  return applyWorkflowConfig(workflow, workflowConfig, { status });
};

/*
Booking workflow default test matrix:

getBookingWorkflow({ carType: "10 FOOTER TRUCK" })
=> Primary true, Domestic true, Import false, Export false, Empty Return false, Empty Pick Up false, Slot Rules false.

getBookingWorkflow({ carType: "20 FOOTER TRUCK", shipmentType: "Domestic Shipment" })
=> Primary true, Domestic true, Import false, Export false, Empty Return false, Empty Pick Up false, Slot Rules false.

getBookingWorkflow({ carType: "40 FOOTER TRUCK", shipmentType: "Domestic Shipment" })
=> Primary true, Domestic true, Import false, Export false, Empty Return false, Empty Pick Up false, Slot Rules false.

getBookingWorkflow({ carType: "20 FOOTER TRUCK", shipmentType: "Import Shipment", status: "COMPLETE" })
=> Primary true, Import true, Empty Return true, Slot Rules true, Slot Direction deduct, Slot Amount 1.

getBookingWorkflow({ carType: "40 FOOTER TRUCK", shipmentType: "Import Shipment", status: "NEW" })
=> Primary true, Import true, Empty Return false, Slot Rules true, Slot Direction deduct, Slot Amount 1.

getBookingWorkflow({ carType: "20 FOOTER TRUCK", shipmentType: "Export Shipment" })
=> Primary true, Export true, Empty Pick Up true, Slot Rules true, Slot Direction add, Slot Amount 1.

getBookingWorkflow({ carType: "40 FOOTER TRUCK", shipmentType: "Export Shipment" })
=> Primary true, Export true, Empty Pick Up true, Slot Rules true, Slot Direction add, Slot Amount 1.

getBookingWorkflow({ carType: "L300/FB/VAN", shipmentType: "Domestic Shipment" })
=> Primary true, Domestic false, Import false, Export false, Empty Return false, Empty Pick Up false, Slot Rules false.
*/
